import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PasswordStrength } from "@/components/PasswordStrength";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { REGIONI, SETTORI, TIPI_PIVA, DIMENSIONI, TIPI_LAVORO, FASCE_RAL } from "@/lib/constants";
import { ArrowRight, ArrowLeft, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";

export default function Register() {
  const nav = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    phone_number: "",
    regione: "",
    tipo_piva: "nessuna",
    codice_ateco: "",
    settore: "",
    dimensione: "solo",
    tipo_lavoro: "",       // solo per dipendenti/pensionati
    fascia_ral: "",         // solo per dipendenti/pensionati
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Deriviamo se l'utente è P.IVA (freelance) o dipendente/pensionato
  const isPIva = form.tipo_piva === "forfettario" || form.tipo_piva === "ordinario";

  const nextStep = () => {
    if (step === 1) {
      if (!form.full_name || !form.email) {
        toast.error("Compila tutti i campi");
        return;
      }
      // Fix P4: password policy min 8 + lettera + numero
      if (form.password.length < 8) {
        toast.error("La password deve avere almeno 8 caratteri");
        return;
      }
      if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
        toast.error("La password deve contenere almeno una lettera e un numero");
        return;
      }
    }
    if (step === 2) {
      if (!form.regione) {
        toast.error("Seleziona una regione per continuare");
        return;
      }
    }
    setStep(step + 1);
  };

  const submit = async () => {
    setLoading(true);
    try {
      // Feature: tracciamento canale — leggi invite_code da localStorage (se presente e formato valido)
      let inviteCode = null;
      try {
        const raw = (localStorage.getItem("tuotax_invito") || "").trim();
        if (raw && /^[A-Za-z0-9-]{1,32}$/.test(raw)) inviteCode = raw;
      } catch {}
      await register({
        email: form.email,
        password: form.password,
        full_name: form.full_name,
        phone_number: form.phone_number,
        regione: form.regione || "",
        tipo_piva: form.tipo_piva,
        // Campi P.IVA — solo se freelance
        codice_ateco: isPIva ? form.codice_ateco : "",
        settore: isPIva ? (form.settore || "") : "",
        dimensione: isPIva ? form.dimensione : "solo",
        // Campi dipendente/pensionato — solo se non-P.IVA
        tipo_lavoro: !isPIva ? form.tipo_lavoro : "",
        fascia_ral: !isPIva ? form.fascia_ral : "",
        invite_code: inviteCode,
      });
      // Cleanup localStorage dopo registrazione riuscita
      try { localStorage.removeItem("tuotax_invito"); } catch {}
      toast.success("Benvenuto in TUOTAX!");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center mb-8" data-testid="register-brand-link">
          <Logo iconSize={36} textClassName="text-xl" />
        </Link>

        {/* Progress */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-primary" : "bg-muted"}`} />
          ))}
        </div>

        <Card className="p-8 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {step === 1 && (
            <>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Passo 1 di 3</div>
              <h1 className="font-heading font-bold text-3xl mb-2">Il tuo account</h1>
              <p className="text-muted-foreground mb-6">Iniziamo dalle basi.</p>
              <div className="space-y-4">
                <div><Label htmlFor="name">Nome completo</Label>
                  <Input id="name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="register-name-input" />
                </div>
                <div><Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="register-email-input" />
                </div>
                <div><Label htmlFor="password">Password <span className="text-xs font-normal text-muted-foreground">(min 8 caratteri, 1 lettera + 1 numero)</span></Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="password"
                      type={showPw ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => set("password", e.target.value)}
                      className="h-12 rounded-xl pr-11"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-muted transition-colors"
                      aria-label={showPw ? "Nascondi password" : "Mostra password"}
                      data-testid="register-password-toggle"
                    >
                      {showPw ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  </div>
                  {/* Fix P4: password strength indicator */}
                  {form.password && (
                    <PasswordStrength password={form.password} />
                  )}
                </div>
                <div><Label htmlFor="phone">Cellulare <span className="text-xs text-muted-foreground font-normal">(opzionale — per notifiche WhatsApp future)</span></Label>
                  <Input id="phone" type="tel" value={form.phone_number || ""} onChange={(e) => set("phone_number", e.target.value)} placeholder="+39 333 1234567" className="h-12 rounded-xl mt-1.5" data-testid="register-phone-input" />
                </div>
                <Button onClick={nextStep} className="w-full h-12 rounded-full" data-testid="register-next-1">
                  Avanti <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Passo 2 di 3</div>
              <h1 className="font-heading font-bold text-3xl mb-2">Dove operi?</h1>
              <p className="text-muted-foreground mb-6">Ci serve per trovare bandi rilevanti per te.</p>
              <div className="space-y-4">
                <div><Label>Regione</Label>
                  <Select value={form.regione || undefined} onValueChange={(v) => set("regione", v)}>
                    <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="register-regione"><SelectValue placeholder="Seleziona regione" /></SelectTrigger>
                    <SelectContent>{REGIONI.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>                <div><Label>Tipo di lavoro</Label>
                  <Select value={form.tipo_piva} onValueChange={(v) => set("tipo_piva", v)}>
                    <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="register-tipo-piva"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPI_PIVA.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {isPIva && (
                  <div><Label>Dimensione dell'attività</Label>
                    <Select value={form.dimensione} onValueChange={(v) => set("dimensione", v)}>
                      <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="register-dimensione"><SelectValue /></SelectTrigger>
                      <SelectContent>{DIMENSIONI.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="rounded-full h-12" data-testid="register-back-2"><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
                  <Button onClick={nextStep} className="flex-1 h-12 rounded-full" data-testid="register-next-2">Avanti <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Passo 3 di 3</div>
              <h1 className="font-heading font-bold text-3xl mb-2">
                {isPIva ? "Cosa fai?" : "Che lavoro fai?"}
              </h1>
              <p className="text-muted-foreground mb-6">
                {isPIva
                  ? "Più dettagli ci dai, meglio filtriamo i bandi e calcoliamo le scadenze."
                  : "Ci serve per calcolare correttamente IRPEF, addizionali e proporti i bonus a cui hai diritto."}
              </p>
              <div className="space-y-4">
                {isPIva ? (
                  <>
                    <div><Label>Settore</Label>
                      <Select value={form.settore || undefined} onValueChange={(v) => set("settore", v)}>
                        <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="register-settore"><SelectValue placeholder="Seleziona settore" /></SelectTrigger>
                        <SelectContent>{SETTORI.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Codice ATECO (opzionale)</Label>
                      <Input value={form.codice_ateco} onChange={(e) => set("codice_ateco", e.target.value)} placeholder="Es. 62.02.00" className="h-12 rounded-xl mt-1.5" data-testid="register-ateco" />
                      <p className="text-xs text-muted-foreground mt-1">Lo trovi sulla tua visura camerale. Non lo sai? Puoi aggiungerlo dopo.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div><Label>Inquadramento</Label>
                      <Select value={form.tipo_lavoro || undefined} onValueChange={(v) => set("tipo_lavoro", v)}>
                        <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="register-tipo-lavoro"><SelectValue placeholder="Seleziona inquadramento" /></SelectTrigger>
                        <SelectContent>{TIPI_LAVORO.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Fascia di reddito annuo lordo</Label>
                      <Select value={form.fascia_ral || undefined} onValueChange={(v) => set("fascia_ral", v)}>
                        <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="register-fascia-ral"><SelectValue placeholder="Seleziona fascia RAL" /></SelectTrigger>
                        <SelectContent>{FASCE_RAL.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">Trovi la RAL sulla tua Certificazione Unica (CU). Serve per stime IRPEF e bonus.</p>
                    </div>
                  </>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(2)} className="rounded-full h-12" data-testid="register-back-3"><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
                  <Button onClick={submit} disabled={loading} className="flex-1 h-12 rounded-full" data-testid="register-submit-button">
                    {loading ? "Creazione..." : "Crea account"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        <p className="text-sm text-muted-foreground mt-6 text-center">
          Hai già un account? <Link to="/login" className="text-primary font-semibold" data-testid="register-to-login">Accedi</Link>
        </p>
      </div>
    </div>
  );
}
